import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Injectable, inject } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom, debounceTime, takeUntil, mergeMap } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AppConstants } from '@aston/foundation';
import { FederationsService } from 'apps/console/src/app/federations-module/services';
import { FederationUserCreateFormComponent } from 'apps/console/src/app/federations-module/components/federation-user-create-form/federation-user-create-form.component';

import { AppStoreActions } from '../app-store';

import * as featureSelectors from './selectors';
import * as featureActions from './actions';

@Injectable({providedIn: 'root'})
export class FederationDetailStoreEffects {
	private actions$ = inject(Actions);
	private store = inject(Store);
	private federationsService = inject(FederationsService);
	protected modalService = inject(NgbModal);


	currentFederationRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadFederationRequest),
		switchMap(action => this.federationsService.getFederation(action.id).pipe(
			map(subsidiary => featureActions.LoadFederationSuccess({ entity: subsidiary }),
				catchError(error => of(featureActions.LoadFederationFailure({ error })))
			))
		)));

	updateFederationRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateFederationRequest),
		withLatestFrom(this.store.select(featureSelectors.selectCurrentFederationId)),
		switchMap(([action, federationId]) => this.federationsService.updateFederation(action.entity).pipe(
			switchMap(_ => [
				featureActions.UpdateFederationSuccess({ entity: action.entity }),
				featureActions.LoadFederationRequest(federationId)
			]),
			catchError(error => of(featureActions.UpdateFederationFailure({ error })))
		))
	));

	loadUsersListRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadUsersListRequest),
		withLatestFrom(
			this.store.select(featureSelectors.selectUsersListSlice),
			this.store.select(featureSelectors.selectCurrentFederationId)),
		debounceTime(500),
		switchMap(([action, usersListSlice, federationId]) => {
			const { filters, paging } = usersListSlice;
			return this.federationsService.getFederationUsers(federationId, { ...paging, filters }).pipe(
				map(list => featureActions.LoadUsersListSuccess({ list, correlationParams: action.correlationParams })),
				catchError(error => of(featureActions.LoadUsersListFailure({ error })))
			)
		})
	));

	UpdateUsersListSettings$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateUsersListSettings),
		map(_ => featureActions.LoadUsersListRequest())
	));

	UpdateTransientUserStatusRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateTransientUserStatusRequest),
		withLatestFrom(this.store.select(featureSelectors.selectCurrentFederationId)),
		switchMap(([action, federationId]) => this.federationsService.updateUserStatus(federationId, action.entity).pipe(
			switchMap(_ => [
				featureActions.UpdateTransientUserStatusSuccess({ entity: action.entity }),
				featureActions.LoadUsersListRequest(),
			]),
			catchError(error => of(featureActions.UpdateTransientUserStatusFailure({ error }), featureActions.LoadUsersListRequest()))
		))
	))

	OnOpenFederationUserForm$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(featureActions.OpenFederationUserForm),
			withLatestFrom(this.store.select(featureSelectors.selectUsersList)),
			map(([_, usersList]) => {
				const modalRef = this.modalService.open(FederationUserCreateFormComponent, AppConstants.DEFAULT_MODAL_OPTIONS);
				const modal = (modalRef.componentInstance as FederationUserCreateFormComponent);

				modal.usersList = usersList.items;
				modal.submitForm.pipe(
					takeUntil(modal.destroySubscriptions$)
				).subscribe((entity: any) => {
					entity.forEach(element => {
						this.store.dispatch(featureActions.CreateFederationUserRequest(element))
						this.store.dispatch(featureActions.SetActiveModal({modal: null}))
					});
				});

				modal.cancelForm.pipe(
					takeUntil(modal.destroySubscriptions$)
				).subscribe(_ => this.store.dispatch(featureActions.SetActiveModal({modal: null})))

				return featureActions.SetActiveModal({modal: modalRef});
			})
		);
	});

	CreateFederationUserRequestEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.CreateFederationUserRequest),
		withLatestFrom(this.store.select(featureSelectors.selectCurrentFederationId)),
		mergeMap(([action, federationId]) => this.federationsService.postFederationUser(federationId, action.entity).pipe(
			switchMap(_ => [
				featureActions.CreateFederationUserSuccess({ entity: action.entity }),
				featureActions.LoadUsersListRequest()
			]),
			catchError(error => of(featureActions.CreateFederationUserFailure({ error }), AppStoreActions.ToastError('Errors.ErrorHasOccurred')))
		))
	));

	UpdateFederationRegistrationRequestRequestEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.ResentUserInvitationRequest),
		withLatestFrom(this.store.select(featureSelectors.selectCurrentFederationId)),
		mergeMap(([action, federationId]) => this.federationsService.resendInvitationToUser(federationId, action.entity.userId).pipe(
			switchMap(_ => [
				featureActions.ResentUserInvitationSuccess({ entity: action.entity }),
				featureActions.LoadUsersListRequest()
			]),
			catchError(error => of(featureActions.ResentUserInvitationtFailure({ error }), AppStoreActions.ToastError('Errors.ErrorHasOccurred')))
		))
	));

	displayEmailAlreadyPresentInGuestListGuestErrorEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.DisplayEmailAlreadyExistInGuestListError),
		map(_ => AppStoreActions.ToastError('Errors.EmailAlreadyExistInGuestList'))
	));

	displayEmailAlreadyPresentErrorEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.DisplayEmailAlreadyExistInUserListError),
		map(_ => AppStoreActions.ToastError('Errors.EmailAlreadyExistInUserList'))
	));
}
